import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken } from "../lib/api";
import RawHeader from "../components/RawHeader";
import HelpDiagram from "../components/HelpDiagram";
import AdminNav from "../components/AdminNav";
import FactoryNotesModal from "../components/FactoryNotesModal";
import AdVideoPanel from "../components/AdVideoPanel";

const fmtGBP = (n) => `£${Number(n || 0).toFixed(2)}`;

const AdminDashboard = () => {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [notesFor, setNotesFor] = useState(null); // product whose factory-notes modal is open
  const [syncStatus, setSyncStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/products");
      setProducts(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminToken(null);
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSnapshot = async () => {
    try {
      const r = await api.get("/admin/finance");
      setSnapshot(r.data?.revenue || null);
    } catch {
      // silent — finance dashboard will surface errors
    }
  };

  useEffect(() => {
    load();
    loadSnapshot();
    // poll parent-sync status every 60s so the green/red dot stays live
    const loadSyncStatus = () => {
      api.get("/admin/parent-sync-status").then((r) => setSyncStatus(r.data)).catch(() => {});
    };
    loadSyncStatus();
    const t = setInterval(loadSyncStatus, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this design permanently?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Delete failed";
      toast.error(`Delete failed: ${detail}`);
      // If the item has no stable id (legacy orphan doc), offer the by-name sweep.
      if (e?.response?.status === 404) {
        const prod = products.find((p) => p.id === id);
        if (prod && window.confirm(`Couldn't find "${prod.name}" by ID. Delete every product named "${prod.name}" at £${prod.price} instead?`)) {
          try {
            const r = await api.post("/admin/products/delete-by-name", {
              name_contains: prod.name,
              price: prod.price,
            });
            toast.success(`Deleted ${r.data.deleted_count} matching design(s)`);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Fallback delete failed");
          }
        }
      }
    }
  };

  const duplicate = async (p) => {
    const newName = window.prompt("Name for the duplicate:", `${p.name} — COPY`);
    if (!newName?.trim()) return;
    try {
      const { id: _i, created_at: _c, updated_at: _u, ...rest } = p;
      await api.post("/admin/products", { ...rest, name: newName, published: false });
      toast.success("Duplicated as draft");
      load();
    } catch {
      toast.error("Duplicate failed");
    }
  };

  const togglePublish = async (p) => {
    const nextState = !p.published;
    try {
      await api.patch(`/admin/products/${p.id}/publish`, { published: nextState });
      toast.success(nextState ? `${p.name} is now LIVE on the shop` : `${p.name} unpublished`);
      // Optimistic update so the badge flips instantly without a full re-fetch
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: nextState } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish toggle failed");
    }
  };

  const bulkPublishAllDrafts = async () => {
    const drafts = products.filter((p) => !p.published);
    if (drafts.length === 0) {
      toast.info("No drafts to publish");
      return;
    }
    if (!window.confirm(`Publish all ${drafts.length} draft(s) to the shop now?`)) return;
    try {
      await Promise.all(
        drafts.map((p) => api.patch(`/admin/products/${p.id}/publish`, { published: true })),
      );
      toast.success(`${drafts.length} designs published to the shop`);
      load();
    } catch {
      toast.error("Bulk publish partly failed — reload and check");
    }
  };

  const promptAndAssignDrop = async (p) => {
    const raw = window.prompt(`Drop number for "${p.name}" (currently Drop ${p.drop_number || 1}):`, String(p.drop_number || 1));
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) {
      toast.error("Drop number must be a whole number ≥ 1");
      return;
    }
    try {
      await api.post("/admin/products/bulk-drop", { product_ids: [p.id], drop_number: n });
      toast.success(`Moved to Drop ${String(n).padStart(2, "0")}`);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, drop_number: n } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drop assign failed");
    }
  };

  const copyManufacturerUrl = async (p) => {
    const shortId = (p.id || "").replace(/-/g, "").slice(0, 12);
    const url = `${window.location.origin}/spec/${shortId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Manufacturer URL copied to clipboard");
    } catch {
      window.prompt("Copy this manufacturer URL:", url);
    }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch {}
    setAdminToken(null);
    nav("/");
  };

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="admin" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// ADMIN_PANEL</div>
            <h1 className="font-display text-3xl sm:text-4xl">DESIGN LIBRARY</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="parent-sync-btn"
              onClick={async () => {
                try {
                  toast.info("Pushing catalogue to RAW Company hub…");
                  const r = await api.post("/admin/parent-sync-now");
                  setSyncStatus(r.data);
                  if (r.data?.ok) toast.success(r.data.message || "Synced");
                  else toast.error(r.data?.message || "Sync failed");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Sync failed");
                }
              }}
              title={
                syncStatus?.at
                  ? `Parent sync ${syncStatus.ok ? "OK" : "FAILED"} at ${syncStatus.at?.slice(0,16).replace("T"," ")} — ${syncStatus.message}`
                  : "Trigger a manual push of the full catalogue to rawcompany.uk"
              }
              className="btn-raw text-xs flex items-center gap-2"
              data-testid="parent-sync-indicator"
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  syncStatus?.ok === true
                    ? "bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.8)]"
                    : syncStatus?.ok === false
                    ? "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]"
                    : "bg-zinc-500"
                }`}
              />
              HUB SYNC
            </button>
            <button data-testid="help-btn" onClick={() => setHelpOpen(true)} className="btn-raw text-xs">? GUIDE</button>
            <a
              data-testid="gmail-link"
              href="https://mail.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-raw text-xs inline-flex items-center gap-1.5"
              title="Open your Gmail inbox in a new tab"
            >
              <span className="text-[#d4af37]">✉</span> GMAIL
            </a>
            <a
              data-testid="stripe-link"
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-raw text-xs inline-flex items-center gap-1.5"
              title="Open your Stripe dashboard in a new tab"
            >
              <span className="text-[#d4af37]">£</span> STRIPE
            </a>
            <button
              data-testid="full-catalogue-spec-btn"
              onClick={async () => {
                const url = `${window.location.origin}/spec-all`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Full-catalogue spec URL copied to clipboard");
                  window.open(url, "_blank", "noopener,noreferrer");
                } catch {
                  window.prompt("Copy this full-catalogue spec URL:", url);
                }
              }}
              className="btn-raw text-xs inline-flex items-center gap-1.5"
              title="Copy + open the /spec-all URL — every design's factory notes + images in one printable page to send to your factory"
            >
              <span className="text-[#d4af37]">✂</span> FULL SPEC URL
            </button>
            <a
              data-testid="download-blueprint-btn"
              href="/api/spec-all/download"
              download
              className="btn-raw text-xs inline-flex items-center gap-1.5"
              title="Download the whole shop as a ZIP of tech-pack PDFs — one PDF per design + an index. Email this file straight to your factory."
            >
              <span className="text-[#d4af37]">⬇</span> BLUEPRINT ZIP
            </a>
            <button
              data-testid="refresh-branding-btn"
              onClick={async () => {
                if (!window.confirm("Refresh ALL designs' factory notes?\n\n• Latest RAW COMPANY branding rules (inside-collar only)\n• Auto-backfills full tech-pack spec on any design missing notes\n• Preserves any existing AI-written tech-pack body")) return;
                try {
                  const r = await api.post("/admin/products/refresh-branding");
                  const bf = r.data.backfilled ?? 0;
                  const up = r.data.updated ?? 0;
                  toast.success(
                    `Updated ${up} design${up === 1 ? "" : "s"}` +
                    (bf > 0 ? ` · Backfilled full tech-pack on ${bf}` : "") +
                    (r.data.skipped ? ` · Skipped ${r.data.skipped}` : "")
                  );
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Refresh failed");
                }
              }}
              className="btn-raw text-xs"
              title="Re-apply the latest RAW COMPANY branding + auto-fill any missing factory notes on all existing designs"
            >
              ↻ FILL ALL NOTES
            </button>
            <button
              data-testid="rebuild-notes-btn"
              onClick={async () => {
                if (!window.confirm("REBUILD every design's factory notes from scratch?\n\n• Uses the latest professional tech-pack template (BOM, POM, grading, sampling flow, certifications)\n• Replaces any old rigid notes that factories complained about\n• Cannot be undone — but the new notes are richer and more factory-friendly\n\nProceed?")) return;
                try {
                  toast.info("Rebuilding notes for every design…");
                  const r = await api.post("/admin/products/refresh-branding?force_rebuild=true");
                  const rb = r.data.rebuilt ?? 0;
                  toast.success(`Rebuilt professional tech-pack on ${rb} design${rb === 1 ? "" : "s"}`);
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Rebuild failed");
                }
              }}
              className="btn-raw text-xs"
              title="Force-replace every design's tech-pack body with the latest professional template — use this if a factory said the notes were too rigid"
            >
              <span className="text-[#d4af37]">⚙</span> REBUILD ALL NOTES
            </button>
            <button
              data-testid="download-all-btn"
              onClick={async () => {
                try {
                  toast.info("Packaging all tech-packs…");
                  const { api: axiosInst } = await import("../lib/api");
                  const r = await axiosInst.get("/admin/products/export-all/zip", {
                    responseType: "blob",
                  });
                  const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
                  const a = document.createElement("a");
                  a.href = url;
                  const stamp = new Date().toISOString().slice(0, 10);
                  a.download = `RAW-techpacks-${stamp}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("ZIP downloaded");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Download failed");
                }
              }}
              className="btn-raw text-xs"
              title="Download every design's PDF tech-pack in one ZIP"
            >
              ⬇ ALL PDFS (ZIP)
            </button>
            <button data-testid="logout-btn" onClick={logout} className="btn-raw text-xs">LOGOUT</button>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="bulk-units-per-size"
                onClick={async () => {
                  if (!window.confirm("Set every product's production order to 5 × S/M/L/XL (20 units per design)?")) return;
                  try {
                    const r = await api.post("/admin/products/bulk-units-per-size", { units_per_size: 5 });
                    toast.success(`${r.data.modified} product(s) set to 20 units each`);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Bulk update failed");
                  }
                }}
                className="btn-raw text-[10px]"
                title="Set every product to 5×S/M/L/XL production order"
              >
                20/DESIGN
              </button>
              <Link to="/admin/design/new" data-testid="new-design-btn" className="btn-raw btn-raw-primary">+ NEW DESIGN</Link>
            </div>
          </div>
        </div>

        {/* Admin Sub-Nav: Finance / Orders / Logo Studio */}
        <AdminNav />

        {/* DRAFTS SECTION — impossible-to-miss list of designs not yet on the shop floor */}
        {products.filter((p) => !p.published).length > 0 && (
          <section
            data-testid="drafts-section"
            className="mb-8 border-2 border-[#d4af37] bg-[#d4af37]/5 p-5 sm:p-6"
          >
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">
                  // DRAFTS · NOT LIVE ON SHOP YET
                </div>
                <h2 className="font-display text-2xl sm:text-3xl text-white">
                  {products.filter((p) => !p.published).length} design{products.filter((p) => !p.published).length === 1 ? "" : "s"} waiting to publish
                </h2>
                <p className="text-xs text-zinc-400 mt-1">
                  These are your saved designs that haven&apos;t hit the shop floor yet. Publish them here.
                </p>
              </div>
              <button
                data-testid="drafts-section-publish-all"
                onClick={bulkPublishAllDrafts}
                className="btn-raw btn-raw-primary text-xs"
              >
                ▶ PUBLISH ALL TO SHOP
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {products.filter((p) => !p.published).map((p) => (
                <div
                  key={p.id}
                  data-testid={`draft-card-${p.id}`}
                  className="border border-[#d4af37]/40 bg-black overflow-hidden flex flex-col"
                >
                  <div className="aspect-square bg-[#050505] border-b border-[#d4af37]/30 overflow-hidden">
                    {p.image_base64 ? (
                      <img
                        src={p.image_base64.startsWith("data:") ? p.image_base64 : `data:image/png;base64,${p.image_base64}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[9px] tracking-[0.3em] uppercase text-zinc-600">no image</div>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-display text-sm text-white truncate">{p.name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
                      {p.clothing_type} · £{Number(p.price || 0).toFixed(2)}
                    </div>
                    <div className="flex gap-1 mt-3">
                      <button
                        data-testid={`draft-publish-${p.id}`}
                        onClick={() => togglePublish(p)}
                        className="flex-1 px-2 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#16a34a] bg-[#16a34a]/10 text-[#16a34a] hover:bg-[#16a34a] hover:text-black"
                      >
                        ▶ PUBLISH
                      </button>
                      <Link
                        to={`/admin/design/${p.id}`}
                        data-testid={`draft-edit-${p.id}`}
                        className="px-2 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-300 hover:border-white hover:bg-white hover:text-black"
                      >
                        EDIT
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* AI Brand-Advert Video (Sora 2) */}
        <AdVideoPanel />

        {/* Live Money Snapshot */}
        <Link
          to="/admin/finance"
          data-testid="money-snapshot"
          className="block mb-8 border border-[#d4af37]/30 bg-[#050505]/80 hover:border-[#d4af37] transition-colors p-4 sm:p-5"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]">
              // LIVE_MONEY_FLOW
            </div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 hover:text-[#d4af37]">
              VIEW FULL FINANCE →
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mt-3 font-mono">
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Today</div>
              <div className="text-xl sm:text-2xl text-[#d4af37] font-display" data-testid="snap-today-gross">
                {fmtGBP(snapshot?.today?.gross)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {snapshot?.today?.orders ?? 0} order{(snapshot?.today?.orders ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1">This Week</div>
              <div className="text-xl sm:text-2xl text-white font-display" data-testid="snap-week-gross">
                {fmtGBP(snapshot?.week?.gross)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {snapshot?.week?.orders ?? 0} order{(snapshot?.week?.orders ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1">This Month</div>
              <div className="text-xl sm:text-2xl text-white font-display" data-testid="snap-month-gross">
                {fmtGBP(snapshot?.month?.gross)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {snapshot?.month?.orders ?? 0} order{(snapshot?.month?.orders ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1">All Time</div>
              <div className="text-xl sm:text-2xl text-white font-display" data-testid="snap-alltime-gross">
                {fmtGBP(snapshot?.all_time?.gross)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {snapshot?.all_time?.orders ?? 0} order{(snapshot?.all_time?.orders ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
          </div>
        </Link>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-10">
          {[
            ["TOTAL_DESIGNS", products.length],
            ["PUBLISHED", products.filter((p) => p.published).length],
            ["DRAFTS", products.filter((p) => !p.published).length],
            ["TOTAL_STOCK", products.reduce((a, p) => a + (p.stock || 0), 0)],
          ].map(([label, val]) => (
            <div key={label} className="bg-[#050505] p-6">
              <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// {label}</div>
              <div className="font-display text-3xl">{val}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING...</div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-20 text-center">
            <div className="text-[#d4af37] text-xs tracking-[0.3em] uppercase mb-4">// NO DESIGNS YET</div>
            <p className="text-zinc-500 mb-6">Create your first clothing design to populate the shop.</p>
            <Link to="/admin/design/new" className="btn-raw btn-raw-primary">+ CREATE FIRST DESIGN</Link>
          </div>
        ) : (
          <>
          {products.some((p) => !p.published) && (
            <div data-testid="drafts-alert" className="mb-4 border border-[#d4af37]/40 bg-[#d4af37]/5 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-xs text-zinc-300">
                <span className="text-[#d4af37] font-mono">
                  {products.filter((p) => !p.published).length}
                </span>{" "}
                draft design{products.filter((p) => !p.published).length === 1 ? "" : "s"} not yet on the shop floor.
              </div>
              <button
                data-testid="publish-all-drafts"
                onClick={bulkPublishAllDrafts}
                className="btn-raw btn-raw-primary text-[10px]"
              >
                ▶ PUBLISH ALL DRAFTS TO SHOP
              </button>
            </div>
          )}
          <div className="border border-zinc-800 overflow-x-auto" data-testid="designs-table">
            <table className="w-full font-mono text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0a0a0a]">
                  {["#", "PREVIEW", "NAME", "TYPE", "PRICE", "STOCK", "DROP", "STATUS", "ACTIONS"].map((h) => (
                    <th key={h} className="text-left p-3 text-[9px] tracking-[0.25em] uppercase text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.id} className="border-b border-zinc-800/50 hover:bg-[#0a0a0a]/50 transition-colors">
                    <td className="p-3 text-zinc-600">{String(i + 1).padStart(3, "0")}</td>
                    <td className="p-3">
                      <div className="w-14 h-14 bg-[#0a0a0a] border border-zinc-800 overflow-hidden">
                        {p.image_base64 ? (
                          <img src={p.image_base64.startsWith("data:") ? p.image_base64 : `data:image/png;base64,${p.image_base64}`} alt="" className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-display text-base">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">{p.id.slice(0, 8)}</div>
                    </td>
                    <td className="p-3 text-zinc-400">{p.clothing_type}</td>
                    <td className="p-3 text-[#d4af37]">£{Number(p.price).toFixed(2)}</td>
                    <td className="p-3 text-zinc-400">{p.stock}</td>
                    <td className="p-3">
                      <button
                        data-testid={`drop-${p.id}`}
                        onClick={() => promptAndAssignDrop(p)}
                        className="px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-300 hover:border-[#d4af37] hover:text-[#d4af37]"
                        title="Click to reassign drop"
                      >
                        DROP {String(p.drop_number || 1).padStart(2, "0")}
                      </button>
                    </td>
                    <td className="p-3">
                      <span data-testid={`status-${p.id}`} className={`px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase border ${p.published ? "text-[#16a34a] border-[#16a34a]" : "text-zinc-500 border-zinc-700"}`}>
                        {p.published ? "LIVE" : "DRAFT"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {!p.published ? (
                          <button
                            data-testid={`publish-${p.id}`}
                            onClick={() => togglePublish(p)}
                            className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#16a34a] bg-[#16a34a]/10 text-[#16a34a] hover:bg-[#16a34a] hover:text-black"
                            title="Publish to shop floor"
                          >
                            ▶ PUBLISH
                          </button>
                        ) : (
                          <button
                            data-testid={`unpublish-${p.id}`}
                            onClick={() => togglePublish(p)}
                            className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-white"
                            title="Hide from shop"
                          >
                            HIDE
                          </button>
                        )}
                        <button
                          data-testid={`notes-${p.id}`}
                          onClick={() => setNotesFor(p)}
                          className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
                          title="View factory notes & email tech-pack"
                        >
                          FACTORY NOTES
                        </button>
                        <button
                          data-testid={`manufacturer-url-${p.id}`}
                          onClick={() => copyManufacturerUrl(p)}
                          className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
                          title="Copy the public /spec/ URL to share with the factory"
                        >
                          MFG URL
                        </button>
                        <Link to={`/admin/design/${p.id}`} data-testid={`edit-${p.id}`} className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-white hover:bg-white hover:text-black">EDIT</Link>
                        <button data-testid={`dup-${p.id}`} onClick={() => duplicate(p)} className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-[#d4af37] hover:text-[#d4af37]">DUP</button>
                        <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-[#d4af37] hover:bg-[#d4af37] hover:text-black">DEL</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      <HelpDiagram open={helpOpen} onClose={() => setHelpOpen(false)} />
      <FactoryNotesModal
        open={!!notesFor}
        product={notesFor}
        onClose={() => setNotesFor(null)}
        onSent={() => load()}
      />
    </div>
  );
};

export default AdminDashboard;
